import React, { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldOff,
  Loader2,
  Copy,
  Check,
  RefreshCw,
} from "lucide-react";

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border hover:border-primary/40 text-muted-foreground hover:text-primary"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function EnableFlow({ onDone }) {
  const [step, setStep] = useState("intro"); // intro | scan | codes
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [codes, setCodes] = useState([]);
  const [savedAck, setSavedAck] = useState(false);
  const [error, setError] = useState("");

  const start = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      setStep("scan");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { code });
      setCodes(data.backup_codes || []);
      setStep("codes");
      toast.success("2FA enabled");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "intro") {
    return (
      <div className="space-y-4" data-testid="2fa-enable-intro">
        <p className="text-sm text-muted-foreground">
          Two-factor authentication adds a 6-digit code from an authenticator app
          (Google Authenticator, 1Password, Authy, Bitwarden…) on top of your
          password.
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button
          onClick={start}
          disabled={submitting}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="2fa-enable-start-button"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enable 2FA"}
        </Button>
      </div>
    );
  }

  if (step === "scan") {
    return (
      <form onSubmit={confirm} className="space-y-5" data-testid="2fa-scan-form">
        <p className="text-sm text-muted-foreground">
          Scan this QR code with your authenticator app, then enter the 6-digit code
          below to confirm.
        </p>
        <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
          <img
            src={setup.qr_data_url}
            alt="2FA QR code"
            data-testid="2fa-qr-image"
            className="w-44 h-44 rounded-lg border border-border bg-white p-2"
          />
          <div className="text-xs text-muted-foreground space-y-2">
            <div>Or enter this secret manually:</div>
            <div className="flex items-center gap-2">
              <code
                className="text-foreground bg-secondary px-2 py-1 rounded text-[11px] break-all"
                data-testid="2fa-secret-value"
              >
                {setup.secret}
              </code>
              <CopyButton value={setup.secret} testid="2fa-secret-copy" />
            </div>
          </div>
        </div>
        <div className="space-y-2 max-w-xs">
          <Label htmlFor="confirmCode">6-digit code</Label>
          <Input
            id="confirmCode"
            inputMode="numeric"
            required
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="tracking-widest text-center text-lg"
            data-testid="2fa-confirm-code-input"
          />
        </div>
        {error && (
          <p role="alert" aria-live="polite" className="text-sm text-destructive" data-testid="2fa-confirm-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="2fa-confirm-button"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & enable"}
        </Button>
      </form>
    );
  }

  // codes step
  return (
    <div className="space-y-5" data-testid="2fa-backup-codes">
      <p className="text-sm">
        <span className="text-primary">Save these backup codes now.</span> Each code
        can be used <b>once</b> to sign in if you lose access to your authenticator.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="2fa-backup-codes-grid">
        {codes.map((c) => (
          <code
            key={c}
            className="text-center bg-secondary border border-border rounded px-2 py-2 text-sm tracking-widest"
          >
            {c}
          </code>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <CopyButton value={codes.join("\n")} testid="2fa-backup-codes-copy" />
      </div>
      <div className="flex items-start gap-2 pt-2">
        <Checkbox
          id="ack"
          checked={savedAck}
          onCheckedChange={(v) => setSavedAck(Boolean(v))}
          data-testid="2fa-backup-codes-ack"
        />
        <Label htmlFor="ack" className="text-sm text-muted-foreground">
          I have saved these codes in a secure place.
        </Label>
      </div>
      <Button
        onClick={onDone}
        disabled={!savedAck}
        className="bg-primary text-primary-foreground hover:bg-primary/90"
        data-testid="2fa-backup-codes-done-button"
      >
        I&rsquo;m done
      </Button>
    </div>
  );
}

function DisableDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/2fa/disable", { password, code });
      toast.success("2FA disabled");
      setOpen(false);
      setPassword("");
      setCode("");
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-destructive/40 text-destructive hover:bg-destructive/10"
          data-testid="2fa-disable-open-button"
        >
          <ShieldOff className="w-4 h-4 mr-2" /> Disable 2FA
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="2fa-disable-dialog">
        <DialogHeader>
          <DialogTitle>Disable two-factor authentication</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Confirm your password and current authenticator code to disable 2FA.
          </p>
          <div className="space-y-2">
            <Label htmlFor="dis-pw">Password</Label>
            <Input
              id="dis-pw"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="2fa-disable-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dis-code">Authenticator code</Label>
            <Input
              id="dis-code"
              inputMode="numeric"
              required
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="2fa-disable-code-input"
            />
          </div>
          {error && (
            <p role="alert" aria-live="polite" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              variant="destructive"
              data-testid="2fa-disable-confirm-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable 2FA"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RegenerateDialog({ onCodes }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [newCodes, setNewCodes] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-backup-codes", { password, code });
      setNewCodes(data.backup_codes || []);
      toast.success("Backup codes regenerated");
      if (onCodes) onCodes(data.backup_codes);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setPassword("");
          setCode("");
          setError("");
          setNewCodes([]);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="2fa-regen-open-button" className="border-border">
          <RefreshCw className="w-4 h-4 mr-2" /> Regenerate backup codes
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="2fa-regen-dialog">
        <DialogHeader>
          <DialogTitle>Regenerate backup codes</DialogTitle>
        </DialogHeader>
        {newCodes.length === 0 ? (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Old backup codes will be invalidated immediately.
            </p>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="2fa-regen-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Authenticator code</Label>
              <Input
                inputMode="numeric"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                data-testid="2fa-regen-code-input"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="2fa-regen-confirm-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate new codes"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="2fa-regen-codes">
            <p className="text-sm text-primary">Save these new backup codes — old ones no longer work.</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {newCodes.map((c) => (
                <code
                  key={c}
                  className="text-center bg-secondary border border-border rounded px-2 py-2 text-sm tracking-widest"
                >
                  {c}
                </code>
              ))}
            </div>
            <CopyButton value={newCodes.join("\n")} testid="2fa-regen-copy" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProfile() {
  const { user, refresh } = useAuth();
  const enabled = !!user?.totp_enabled;
  const [enableMode, setEnableMode] = useState(false);

  return (
    <AdminLayout>
      <div className="space-y-10" data-testid="admin-profile">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary/80">Profile</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Your account</h1>
        </div>

        <div className="border border-border rounded-xl bg-card p-6 space-y-3" data-testid="profile-info-card">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-widest mb-1">Email</div>
              <div data-testid="profile-email">{user?.email}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-widest mb-1">Role</div>
              <div data-testid="profile-role">{user?.role}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-widest mb-1">2FA</div>
              <div
                className={enabled ? "text-primary" : "text-amber-400"}
                data-testid="profile-2fa-status"
              >
                {enabled ? "Active" : "Disabled"}
              </div>
            </div>
          </div>
        </div>

        <div className="border border-border rounded-xl bg-card p-6 space-y-4" data-testid="profile-2fa-card">
          <div className="flex items-center gap-3">
            {enabled ? (
              <ShieldCheck className="w-5 h-5 text-primary" />
            ) : (
              <ShieldOff className="w-5 h-5 text-amber-400" />
            )}
            <h2 className="font-display text-2xl">Two-factor authentication</h2>
          </div>

          {!enabled && !enableMode && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                2FA is currently <span className="text-amber-400">disabled</span>. Enable it now.
              </p>
              <Button
                onClick={() => setEnableMode(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="2fa-enable-button"
              >
                <ShieldCheck className="w-4 h-4 mr-2" /> Enable 2FA
              </Button>
            </div>
          )}

          {!enabled && enableMode && (
            <EnableFlow
              onDone={async () => {
                setEnableMode(false);
                await refresh();
              }}
            />
          )}

          {enabled && (
            <div className="space-y-4" data-testid="2fa-enabled-actions">
              <p className="text-sm text-muted-foreground">
                2FA is <span className="text-primary">active</span> on your account.
              </p>
              <div className="flex flex-wrap gap-3">
                <RegenerateDialog onCodes={async () => { await refresh(); }} />
                <DisableDialog onDone={async () => { await refresh(); }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
